/**
 * RagPage — base de conhecimento local (F11).
 *
 * Três telas num arquivo, porque compartilham o mesmo vocabulário:
 *  - lista de tipos de RAG + criação (rag_local/admin_dashboard.html e
 *    create_type.html);
 *  - documentos de um tipo, upload, exclusão e importação de acórdãos do
 *    TCU (manage_type.html);
 *  - chunks de um documento (view_chunks.html).
 *
 * O RAG local é opcional: sem as dependências instaladas, a tela diz isso
 * em vez de estourar.
 */

import {
  faArrowLeft,
  faCheckCircle,
  faCloudUploadAlt,
  faCubes,
  faDatabase,
  faDownload,
  faExclamationTriangle,
  faFileLines,
  faLandmark,
  faPlus,
  faSpinner,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { Link, useParams } from 'react-router'
import {
  type AcordaoTcu,
  type VarreduraTcu,
  chunksDoDocumento,
  criarTipoDeRag,
  detalheTipoDeRag,
  enviarDocumentoRag,
  excluirDocumentosRag,
  importarAcordaosTcu,
  previaAcordaosTcu,
  tiposDeRag,
} from '../../api/rag'
import { useToast } from '../../components/Toast'

const CAMPO =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100'

function tamanho(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function Cabecalho({
  titulo,
  subtitulo,
  voltar,
}: {
  titulo: string
  subtitulo: string
  voltar?: { para: string; rotulo: string }
}) {
  return (
    <header className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
      <div className="bg-gradient-to-r from-purple-700 via-purple-800 to-indigo-900 p-6 text-white">
        {voltar && (
          <Link
            to={voltar.para}
            className="mb-2 inline-flex items-center gap-2 text-sm text-purple-200 hover:text-white"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            {voltar.rotulo}
          </Link>
        )}
        <h1 className="flex items-center gap-3 text-2xl font-bold">
          <FontAwesomeIcon icon={faDatabase} />
          {titulo}
        </h1>
        <p className="mt-1 text-purple-200">{subtitulo}</p>
      </div>
    </header>
  )
}

// ── Lista de tipos ──────────────────────────────────────────────────────────

export function RagPage() {
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const [nome, setNome] = useState('')
  const [descricao, setDescricao] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['rag', 'tipos'],
    queryFn: tiposDeRag,
  })

  const criar = useMutation({
    mutationFn: () => criarTipoDeRag(nome, descricao),
    onSuccess: () => {
      notificar('success', 'Tipo de RAG criado.')
      setNome('')
      setDescricao('')
      void clienteQuery.invalidateQueries({ queryKey: ['rag'] })
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  return (
    <div className="mx-auto max-w-6xl">
      <Cabecalho
        titulo="Base de conhecimento (RAG)"
        subtitulo="Legislação, decretos e jurisprudência que sustentam o que os agentes escrevem."
      />

      {data && !data.disponivel && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5" />
          <span>
            O RAG local não está disponível neste ambiente — faltam as
            dependências (qdrant-client, spacy…).
          </span>
        </div>
      )}

      <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faPlus} className="text-purple-600" />
          Novo tipo
        </h2>
        <form
          className="grid grid-cols-1 gap-4 md:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault()
            criar.mutate()
          }}
        >
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Nome <span className="text-red-500">*</span>
            <input
              id="nome"
              required
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="LICITIA, SUS, TCU_JURISPRUDENCIA…"
              className={`mt-1 ${CAMPO}`}
            />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 md:col-span-2">
            Descrição
            <input
              id="descricao"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              className={`mt-1 ${CAMPO}`}
            />
          </label>
          <button
            type="submit"
            disabled={criar.isPending || !data?.disponivel}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-3 font-semibold text-white shadow-lg disabled:opacity-50"
          >
            <FontAwesomeIcon
              icon={criar.isPending ? faSpinner : faPlus}
              spin={criar.isPending}
            />
            Criar tipo
          </button>
        </form>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
          Tipos cadastrados
        </h2>
        {isLoading ? (
          <p className="text-sm text-slate-500">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            Carregando…
          </p>
        ) : (data?.tipos ?? []).length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-600">
            Nenhum tipo de RAG cadastrado.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {(data?.tipos ?? []).map((t) => (
              <Link
                key={t.id}
                to={`/super-admin/rag/${t.id}`}
                className="rounded-xl border border-slate-200 p-4 transition-all hover:border-purple-400 hover:shadow-md dark:border-slate-700"
              >
                <p className="font-bold text-slate-800 dark:text-slate-100">
                  {t.nome}
                </p>
                <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">
                  {t.descricao || 'Sem descrição'}
                </p>
                <div className="flex gap-4 text-xs text-slate-600 dark:text-slate-400">
                  <span>
                    <FontAwesomeIcon icon={faFileLines} className="mr-1" />
                    {t.documentos} documento(s)
                  </span>
                  <span>
                    <FontAwesomeIcon icon={faCubes} className="mr-1" />
                    {t.chunks} chunk(s)
                  </span>
                  <span className="text-slate-400">{t.colecao}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

// ── Documentos de um tipo ───────────────────────────────────────────────────

export function RagTipoPage() {
  const { tipoId } = useParams()
  const id = Number(tipoId)
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const entrada = useRef<HTMLInputElement>(null)
  const [marcados, setMarcados] = useState<number[]>([])
  const [quantidade, setQuantidade] = useState('10')
  const [ano, setAno] = useState('')
  const [previa, setPrevia] = useState<AcordaoTcu[] | null>(null)
  const [varredura, setVarredura] = useState<VarreduraTcu | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['rag', 'tipo', id],
    queryFn: () => detalheTipoDeRag(id),
    retry: false,
  })

  const recarregar = () =>
    clienteQuery.invalidateQueries({ queryKey: ['rag'] })

  const enviar = useMutation({
    mutationFn: (arquivo: File) => enviarDocumentoRag(id, arquivo),
    onSuccess: (r) => {
      notificar('success', `${r.nome}: ${r.chunks} chunk(s) indexados.`)
      void recarregar()
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const excluir = useMutation({
    mutationFn: () => excluirDocumentosRag(marcados),
    onSuccess: (r) => {
      notificar(
        r.falhas ? 'error' : 'success',
        `${r.removidos} documento(s) removidos${r.falhas ? `, ${r.falhas} falha(s)` : ''}.`,
      )
      setMarcados([])
      void recarregar()
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const prever = useMutation({
    mutationFn: () => previaAcordaosTcu(Number(quantidade), ano, id),
    onSuccess: (r) => {
      setPrevia(r.acordaos ?? [])
      setVarredura(r.varredura ?? null)
      notificar('success', `${(r.acordaos ?? []).length} acórdão(s) na prévia.`)
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const importar = useMutation({
    mutationFn: () => importarAcordaosTcu(id, Number(quantidade), ano),
    onSuccess: (r) => {
      notificar('success', r.message ?? 'Importação concluída.')
      setPrevia(null)
      void recarregar()
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  if (isLoading) {
    return (
      <p className="p-8 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando…
      </p>
    )
  }
  if (isError || !data) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-dark-card">
        <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          Tipo de RAG não encontrado
        </h1>
        <Link to="/super-admin/rag" className="mt-4 inline-block text-purple-600">
          Voltar
        </Link>
      </div>
    )
  }

  const ehTcu = data.tipo.nome.toUpperCase().includes('TCU')

  return (
    <div className="mx-auto max-w-6xl">
      <Cabecalho
        titulo={data.tipo.nome}
        subtitulo={`${data.tipo.documentos} documento(s) · ${data.tipo.chunks} chunk(s) · coleção ${data.tipo.colecao}`}
        voltar={{ para: '/super-admin/rag', rotulo: 'Base de conhecimento' }}
      />

      <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faCloudUploadAlt} className="text-purple-600" />
          Enviar documento
        </h2>
        <div className="rounded-2xl border-2 border-dashed border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50 p-8 text-center dark:border-purple-700 dark:from-purple-900/20 dark:to-indigo-900/20">
          <button
            type="button"
            onClick={() => entrada.current?.click()}
            disabled={enviar.isPending}
            className="text-lg font-semibold text-purple-600 hover:text-purple-700 disabled:opacity-50 dark:text-purple-400"
          >
            {enviar.isPending ? 'Processando…' : 'Escolher arquivo'}
          </button>
          <input
            ref={entrada}
            id="documento"
            type="file"
            accept=".pdf,.docx,.txt,.html,.htm,.md,.markdown"
            className="hidden"
            onChange={(e) => {
              const arquivo = e.target.files?.[0]
              if (arquivo) enviar.mutate(arquivo)
              e.target.value = ''
            }}
          />
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            PDF, DOCX, TXT, HTML ou Markdown · até 100MB. A extração, o
            fatiamento e a indexação rodam no envio.
          </p>
        </div>
      </section>

      {ehTcu && (
        <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
          <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
            <FontAwesomeIcon icon={faLandmark} className="text-purple-600" />
            Importar acórdãos do TCU
          </h2>
          <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
            Direto da API de dados abertos do Tribunal. A prévia LÊ o
            inteiro teor de cada acórdão para dizer se ele trata de
            licitação — e mostra o trecho que sustenta o veredito.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Quantidade
              <input
                type="number"
                min={1}
                max={100}
                value={quantidade}
                onChange={(e) => setQuantidade(e.target.value)}
                className={`mt-1 w-28 ${CAMPO}`}
              />
            </label>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Ano (opcional)
              <input
                type="number"
                min={2000}
                max={2035}
                placeholder="mais recentes"
                value={ano}
                onChange={(e) => setAno(e.target.value)}
                className={`mt-1 w-36 ${CAMPO}`}
              />
            </label>
            <button
              type="button"
              onClick={() => prever.mutate()}
              disabled={prever.isPending}
              className="inline-flex items-center gap-2 rounded-xl border border-purple-500 px-6 py-3 font-medium text-purple-700 hover:bg-purple-50 disabled:opacity-50 dark:text-purple-300"
            >
              <FontAwesomeIcon
                icon={prever.isPending ? faSpinner : faDownload}
                spin={prever.isPending}
              />
              Ver prévia
            </button>
            <button
              type="button"
              onClick={() => {
                if (confirm(`Importar ${quantidade} acórdão(s)?`))
                  importar.mutate()
              }}
              disabled={importar.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-3 font-semibold text-white shadow-lg disabled:opacity-50"
            >
              <FontAwesomeIcon
                icon={importar.isPending ? faSpinner : faCheckCircle}
                spin={importar.isPending}
              />
              Importar
            </button>
          </div>

          {varredura && varredura.motivo !== 'quantidade atingida' && (
            <p className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5" />
              <span>
                A API do TCU não filtra por ano: a varredura leu{' '}
                {varredura.registros_lidos} registro(s) em {varredura.paginas}{' '}
                página(s) e chegou até{' '}
                {varredura.anos_encontrados.join(', ') || '—'} (
                {varredura.motivo}). Para anos mais antigos, envie o PDF do
                acórdão pelo upload acima.
              </span>
            </p>
          )}

          {previa && (
            <ul className="mt-4 max-h-72 space-y-2 overflow-y-auto">
              {previa.length === 0 ? (
                <li className="text-sm text-slate-500">
                  Nada novo para importar com esses filtros.
                </li>
              ) : (
                previa.map((a, i) => (
                  <li
                    key={i}
                    className="rounded-lg border border-slate-200 p-3 text-sm dark:border-slate-700"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-800 dark:text-slate-100">
                        Acórdão {a.numero}/{a.ano}
                        {a.colegiado ? ` — ${a.colegiado}` : ''}
                      </span>
                      {a.veredito === 'relevante' ? (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                          trata de licitação
                        </span>
                      ) : a.veredito === 'irrelevante' ? (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                          outro tema
                        </span>
                      ) : (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                          inteiro teor não pôde ser lido
                        </span>
                      )}
                      {a.lido_do_pdf && (
                        <span className="text-[10px] uppercase tracking-wide text-slate-400">
                          lido do inteiro teor
                        </span>
                      )}
                      {a.ja_existe && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                          já indexado
                        </span>
                      )}
                    </div>
                    {a.titulo && (
                      <span className="block truncate text-xs text-slate-500">
                        {a.titulo}
                      </span>
                    )}
                    {(a.termos ?? []).length > 0 && (
                      <span className="mt-1 flex flex-wrap gap-1">
                        {(a.termos ?? []).map((t) => (
                          <span
                            key={t}
                            className="rounded bg-emerald-50 px-1.5 text-[11px] text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"
                          >
                            {t}
                          </span>
                        ))}
                      </span>
                    )}
                    {(a.trecho || a.sumario) && (
                      <span className="mt-1 block text-xs italic text-slate-500">
                        “{a.trecho || a.sumario}”
                      </span>
                    )}
                  </li>
                ))
              )}
            </ul>
          )}
        </section>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            Documentos
          </h2>
          <button
            type="button"
            onClick={() =>
              setMarcados(
                marcados.length === data.documentos.length
                  ? []
                  : data.documentos.map((d) => d.id),
              )
            }
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 dark:border-slate-600 dark:text-slate-300"
          >
            {marcados.length === data.documentos.length
              ? 'Desmarcar todos'
              : 'Marcar todos'}
          </button>
          <button
            type="button"
            disabled={!marcados.length || excluir.isPending}
            onClick={() => {
              if (confirm(`Excluir ${marcados.length} documento(s) do índice?`))
                excluir.mutate()
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40"
          >
            <FontAwesomeIcon
              icon={excluir.isPending ? faSpinner : faTrashAlt}
              spin={excluir.isPending}
            />
            Excluir selecionados
          </button>
        </div>

        {data.documentos.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-600">
            Nenhum documento neste tipo.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-600">
                  <th className="py-2 pr-4" />
                  <th className="py-2 pr-4">Documento</th>
                  <th className="py-2 pr-4">Tamanho</th>
                  <th className="py-2 pr-4">Chunks</th>
                  <th className="py-2 pr-4">Situação</th>
                  <th className="py-2">Enviado em</th>
                </tr>
              </thead>
              <tbody>
                {data.documentos.map((d) => (
                  <tr
                    key={d.id}
                    className="border-b border-slate-100 dark:border-slate-700"
                  >
                    <td className="py-2 pr-4">
                      <input
                        type="checkbox"
                        aria-label={`Selecionar ${d.nome}`}
                        checked={marcados.includes(d.id)}
                        onChange={(e) =>
                          setMarcados(
                            e.target.checked
                              ? [...marcados, d.id]
                              : marcados.filter((x) => x !== d.id),
                          )
                        }
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <Link
                        to={`/super-admin/rag/documentos/${d.id}`}
                        className="font-medium text-purple-600 hover:underline dark:text-purple-400"
                      >
                        {d.nome}
                      </Link>
                      {d.tipo_arquivo && (
                        <span className="ml-2 rounded bg-slate-100 px-1.5 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                          {d.tipo_arquivo}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                      {tamanho(d.tamanho)}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                      {d.chunks}
                    </td>
                    <td className="py-2 pr-4">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${
                          d.situacao === 'processed'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                        }`}
                      >
                        {d.situacao === 'processed' ? 'indexado' : d.situacao}
                      </span>
                    </td>
                    <td className="py-2 text-slate-600 dark:text-slate-400">
                      {d.criado_em}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

// ── Chunks de um documento ──────────────────────────────────────────────────

export function RagChunksPage() {
  const { documentoId } = useParams()
  const id = Number(documentoId)
  const [busca, setBusca] = useState('')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['rag', 'chunks', id],
    queryFn: () => chunksDoDocumento(id),
    retry: false,
  })

  if (isLoading) {
    return (
      <p className="p-8 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando os chunks…
      </p>
    )
  }
  if (isError || !data) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-dark-card">
        <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          Documento não encontrado
        </h1>
        <Link to="/super-admin/rag" className="mt-4 inline-block text-purple-600">
          Voltar
        </Link>
      </div>
    )
  }

  const termo = busca.trim().toLowerCase()
  const visiveis = termo
    ? data.chunks.filter((c) => c.texto.toLowerCase().includes(termo))
    : data.chunks

  return (
    <div className="mx-auto max-w-5xl">
      <Cabecalho
        titulo={data.documento.nome}
        subtitulo={`${data.chunks.length} chunk(s) indexados${data.tipo_nome ? ` em ${data.tipo_nome}` : ''}`}
        voltar={
          data.tipo_id
            ? { para: `/super-admin/rag/${data.tipo_id}`, rotulo: data.tipo_nome }
            : { para: '/super-admin/rag', rotulo: 'Base de conhecimento' }
        }
      />

      <input
        type="search"
        aria-label="Buscar no conteúdo dos chunks"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        placeholder="Buscar no conteúdo indexado…"
        className={`mb-4 ${CAMPO}`}
      />

      <div className="space-y-3">
        {visiveis.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-600">
            Nenhum trecho encontrado.
          </p>
        ) : (
          visiveis.map((c) => (
            <article
              key={c.id}
              className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-dark-card"
            >
              <div className="mb-2 flex items-center gap-3 text-xs text-slate-500">
                <span className="rounded bg-purple-100 px-2 py-0.5 font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                  #{c.indice}
                </span>
                <span>{c.caracteres} caracteres</span>
                {c.metadados && (
                  <span className="truncate font-mono">{c.metadados}</span>
                )}
              </div>
              <p className="whitespace-pre-line text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                {c.texto}
              </p>
            </article>
          ))
        )}
      </div>
    </div>
  )
}
